import type { ApiError, EventSummary, EventWithRaces, SearchResult, WeekendsResponse } from "@shared/types";

const BASE_URL = import.meta.env.VITE_API_URL || "/api";
/** The API base, for public requests made without the login (so a CDN can share their answers). */
export const API_BASE = BASE_URL;

class ApiClient {
  private accessToken: string | null = null;
  private refreshPromise: Promise<void> | null = null;

  setAccessToken(token: string | null) {
    this.accessToken = token;
  }

  private async request<T>(
    path: string,
    options: RequestInit = {},
    retry = true,
    asText = false
  ): Promise<T> {
    const headers: Record<string, string> = {
      ...(options.headers as Record<string, string>),
    };

    // Don't set Content-Type for FormData (multipart)
    if (!(options.body instanceof FormData)) {
      headers["Content-Type"] = "application/json";
    }

    if (this.accessToken) {
      headers["Authorization"] = `Bearer ${this.accessToken}`;
    }

    const response = await fetch(`${BASE_URL}${path}`, {
      ...options,
      headers,
      credentials: "include", // send cookies (refresh token)
    });

    if (!response.ok) {
      // Attempt auto-refresh on 401, but only once and not for auth endpoints
      if (
        response.status === 401 &&
        retry &&
        !path.startsWith("/auth/refresh") &&
        !path.startsWith("/auth/login")
      ) {
        try {
          await this.attemptRefresh();
          // Retry the original request with new token
          return this.request<T>(path, options, false, asText);
        } catch {
          // Refresh failed — propagate the 401
        }
      }

      const error: ApiError = await response.json().catch(() => ({
        error: response.statusText || "Request failed",
        code: "UNKNOWN",
      }));

      throw new ApiClientError(response.status, error.error, error.code, error);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    if (asText) {
      return (await response.text()) as T;
    }

    return response.json();
  }

  /**
   * Attempt a single token refresh. Deduplicates concurrent refresh attempts.
   */
  private async attemptRefresh(): Promise<void> {
    if (this.refreshPromise) {
      return this.refreshPromise;
    }

    this.refreshPromise = (async () => {
      try {
        const response = await fetch(`${BASE_URL}/auth/refresh`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
        });

        if (!response.ok) {
          this.accessToken = null;
          throw new Error("Refresh failed");
        }

        const data = await response.json();
        this.accessToken = data.accessToken;
      } finally {
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  get<T>(path: string, options?: { signal?: AbortSignal }) {
    return this.request<T>(path, options);
  }

  /**
   * Send a request with the same auth and refresh handling, and return the
   * Response itself (for headers, 304s or non-JSON bodies). Resolves for 2xx
   * and 304; throws ApiClientError (with the parsed body as payload) otherwise.
   */
  async fetchResponse(path: string, init: RequestInit = {}, retry = true): Promise<Response> {
    const headers: Record<string, string> = { ...(init.headers as Record<string, string>) };
    if (this.accessToken) headers["Authorization"] = `Bearer ${this.accessToken}`;
    const response = await fetch(`${BASE_URL}${path}`, { ...init, headers, credentials: "include" });
    if (response.ok || response.status === 304) return response;
    if (response.status === 401 && retry) {
      try {
        await this.attemptRefresh();
        return this.fetchResponse(path, init, false);
      } catch {
        // Refresh failed — propagate the 401
      }
    }
    const error: ApiError = await response.json().catch(() => ({
      error: response.statusText || "Request failed",
      code: "UNKNOWN",
    }));
    throw new ApiClientError(response.status, error.error, error.code, error);
  }

  /** GET a text response (CSV etc.) with the same auth and refresh handling. */
  getText(path: string) {
    return this.request<string>(path, {}, true, true);
  }

  post<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: "POST",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  postForm<T>(path: string, formData: FormData) {
    return this.request<T>(path, {
      method: "POST",
      body: formData,
    });
  }

  put<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: "PUT",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  patch<T>(path: string, body?: unknown) {
    return this.request<T>(path, {
      method: "PATCH",
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  delete<T>(path: string) {
    return this.request<T>(path, { method: "DELETE" });
  }
}

export class ApiClientError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    /** The parsed error body, for endpoints that return extra detail. */
    public payload?: unknown
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

export const api = new ApiClient();

// ─── Event API ───────────────────────────────────────────────────────────────

export interface EventsResponse {
  events: EventSummary[];
  freeAccessRaceIds: string[];
}

export function fetchEvents(params?: {
  series?: string;
  season?: string;
}): Promise<EventsResponse> {
  const qs = new URLSearchParams();
  if (params?.series) qs.set("series", params.series);
  if (params?.season) qs.set("season", params.season);
  const query = qs.toString();
  return api.get<EventsResponse>(`/events${query ? `?${query}` : ""}`);
}

export function fetchWeekends(): Promise<WeekendsResponse> {
  return api.get<WeekendsResponse>("/events/weekends");
}

export function fetchEvent(eventId: string): Promise<EventWithRaces> {
  return api.get<EventWithRaces>(`/events/${eventId}`);
}

// ─── Search API ─────────────────────────────────────────────────────────────

export interface SearchResponse {
  events: SearchResult[];
  freeAccessRaceIds: string[];
}

export async function searchEvents(
  params: { q?: string; series?: string; season?: string },
  signal?: AbortSignal,
): Promise<SearchResponse> {
  const qs = new URLSearchParams();
  if (params.q) qs.set("q", params.q);
  if (params.series) qs.set("series", params.series);
  if (params.season) qs.set("season", params.season);
  const query = qs.toString();
  try {
    const res = await fetch(`${BASE_URL}/search${query ? `?${query}` : ""}`, { signal });
    if (!res.ok) return { events: [], freeAccessRaceIds: [] };
    const data = await res.json();
    return { events: data.events ?? [], freeAccessRaceIds: data.freeAccessRaceIds ?? [] };
  } catch {
    return { events: [], freeAccessRaceIds: [] };
  }
}
